/**
 * MessageList — the scrolling message area (STORY-002-009 step 3, restyled for
 * the design revamp).
 *
 * Renders, in order:
 *   1. persisted thread messages (from TanStack Query — `GET /chat/threads/{id}`)
 *   2. the in-flight assistant message (`currentAssistantText` from the store)
 *   3. a terminal error slot when the stream failed (AC #5/#6/#13)
 *
 * Design revamp (`docs/GardEn/handoff-2026-06/garden/screens-chat.jsx`):
 *   • Assistant turns show a sparkles Avatar disc beside a surface bubble
 *     (border + `--g-shadow-sm`, asymmetric radius with a bottom-left tail).
 *   • User turns are a `bg-primary` / on-primary bubble with a bottom-right tail.
 *   • A streaming caret (`.g-caret` `▍`) trails the in-flight assistant text;
 *     the pre-token state is the step running right now (SCRUM-281's
 *     StageIndicator), with the typing dots only until the first stage
 *     arrives; a stage after the text renders under it in place of the caret.
 *     When 4 s pass with no frame at all, the dots gain «Соединяемся…»
 *     beside them (ConnectingHint, owner-approved 2026-10-13).
 *   • Under a grounded answer: inline `seller_match` mini-cards, a marketplace
 *     product-card carousel, a "Источник" citation chip (opens a BottomSheet
 *     with the cited KB article), and follow-up Pills that re-send on click.
 *   • Empty state: the plain sheet (heading + greeting, plus the one-line
 *     intro for a guest) over the order pad — the «Спросите | Что решит» head
 *     and three starter rows (terms / budget / order), each sending its label.
 *     The quote upload row was dropped on 2026-10-15 (see STARTER_PROMPTS).
 *     The owner removed the pad
 *     on 2026-10-08 and restored it exactly as before on 2026-10-09 (ADR-053).
 *
 * Streaming reveal (AC #2): each freshly-arrived token is wrapped in a
 * `<motion.span>` fading `opacity 0 → 1` over 0.05s. We only animate the tail
 * (newest token); already-revealed text renders statically so a 1000-token
 * response doesn't mount 1000 animating nodes. No width/height/layout anim.
 *
 * Auto-scroll (AC #11): before each append we check whether the user is pinned
 * to the bottom. If they scrolled up we do NOT yank them back down.
 *
 * Markdown (AC #9): assistant content renders via the dependency-free,
 * no-`dangerouslySetInnerHTML` `<Markdown>` renderer. Card labels / seller names
 * / article text are DB text rendered AS text.
 *
 * THE DESIGN FRAME (2026-08, assistant-surface Task 9). Everything above is
 * what this file renders below 1200px, or in dark theme, and it renders there
 * unchanged. Inside the imported design's frame (`useAssistantDesign`) the
 * transcript takes the design's own composition instead (spec §4.3): a 780px
 * column, day dividers, the diamond avatar, both bubbles back with their
 * asymmetric radii, and — the point of the whole surface — the
 * `EstimateCheckCard` in place of `ChatMaterialsTable`. Every branch in this
 * file reads that ONE boolean; none of them reads a viewport of its own.
 */
import { Icon } from "@garden/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Variants,
} from "framer-motion";
import {
  ArrowUpRight,
  Check,
  ChevronDown,
  ClipboardPaste,
  Copy,
  Paperclip,
  RefreshCw,
  RotateCw,
} from "lucide-react";

import { iconProps } from "@/lib/icons";
import { MOTION } from "@/lib/motion";
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { fmtNum, parseDecimal } from "@/lib/format";
import { listProjects } from "@/lib/garden-api";

import { ChatMaterialsTable } from "@/features/chat/components/ChatMaterialsTable";
import {
  ChatProductCards,
  ChatProductCardsDesign,
} from "@/features/chat/components/ChatProductCards";
import { CitationChip } from "@/features/chat/components/CitationChip";
import { EstimateCheckCard } from "@/features/chat/components/EstimateCheckCard";
import { QuantityCalculatorCard } from "@/features/chat/components/QuantityCalculatorCard";
import { FollowupPills } from "@/features/chat/components/FollowupPills";
import { Markdown } from "@/features/chat/components/Markdown";
import { MessageAttachments } from "@/features/chat/components/MessageAttachments";
import { MessageFeedback } from "@/features/chat/components/MessageFeedback";
import { RecommendationPlan } from "@/features/chat/components/RecommendationPlan";
import { SellerCardsRow } from "@/features/chat/components/SellerCardsRow";
import { ANSWER_TEXT_TEST_ID, measureAnswerEndScrollTop } from "@/features/chat/answer-anchor";
import { ConnectingHint } from "@/features/chat/components/ConnectingHint";
import { StageIndicator } from "@/features/chat/components/StageIndicator";
import { TypingIndicator } from "@/features/chat/components/TypingIndicator";
import { dateLocaleFor } from "@/features/chat/ThreadListItem";
import { formatMessageTime } from "@/features/chat/message-time";
import { useAssistantDesign } from "@/features/chat/useDesignFrame";
import type { ChatStreamError, LocalTurn } from "@/features/chat/chat-store";
import {
  isSellerMatchCard,
  type CitationEvent,
  type MaterialsEvent,
  type Need,
  type ProductCardData,
  type SellerMatchCardData,
  type StageEvent,
} from "@/features/chat/useChatStream";
import type { ClarifyRoundEvent, PersistedClarifyRound } from "@/features/chat/clarify/types";
import type { MessageAttachment } from "@/features/chat/attachment-types";

/**
 * A persisted `{kind:"materials"}` entry in an assistant turn's `cards_json`
 * (chat document attachments). The backend persists the sourced quote here as a
 * single entry so a thread reload re-renders the in-chat materials table. It
 * MUST be routed to `ChatMaterialsTable`, never the product-card bucket.
 */
export interface MaterialsCard {
  kind: "materials";
  quote: MaterialsEvent;
}

/** Type guard: is a persisted `cards_json` entry the materials table entry? */
export function isMaterialsCard(
  card: ProductCardData | SellerMatchCardData | MaterialsCard,
): card is MaterialsCard {
  return (
    typeof card === "object" &&
    card !== null &&
    (card as { kind?: unknown }).kind === "materials"
  );
}

/** One persisted message (mirrors backend `ThreadMessage`, STORY-002-005). */
export interface ThreadMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  intent: string | null;
  model: string | null;
  created_at: string;
  /** Structured cards for a persisted turn (mirrors backend `cards_json`) —
   * marketplace product cards AND/OR quote `seller_match` cards AND/OR a single
   * `{kind:"materials"}` quote entry (chat document attachments); null/absent on
   * every other message. */
  cards_json?: (ProductCardData | SellerMatchCardData | MaterialsCard)[] | null;
  /** Attachments persisted on a USER turn (chat document attachments) — the
   * in-bubble image thumbs / file cards on a thread reload. */
  attachments?: MessageAttachment[] | null;
  /** The Recommendation Plan's `needs` extracted from THIS turn's answer
   * (Smart Recommendations R1) — mirrors backend `chat_messages.plan_json`.
   * Rehydrates the plan chip row (`RecommendationPlan`) on a thread reload;
   * null/absent on every turn with no extracted plan. */
  plan_json?: Need[] | null;
  /** The Recommendation-Plan Need this turn resolved (a `fetch_need` tap or a
   * §3.2b typed resolution) — mirrors backend `chat_messages.need_id`. Lets
   * reload mark the exact plan chip "done" (no label-match heuristic);
   * null/absent on every turn that resolved no need. */
  need_id?: string | null;
  /** The clarifying-question round this assistant row asked (contract C8);
   * null/absent on every other row. An `open` round on the latest assistant
   * row re-docks the card on reload (W3); answered, skipped and dismissed
   * rounds render nothing extra — the summary bubble is the record. */
  clarify_round?: PersistedClarifyRound | null;
}

const SCROLL_BOTTOM_THRESHOLD_PX = 48;

/** Stable empty-set reference (Smart Recommendations R1) — avoids allocating
 * a fresh `Set` every render when a caller omits an explored/loading/errored
 * prop. */
const EMPTY_NEED_SET: ReadonlySet<string> = new Set();

/** The empty-state starters (design icons tile/layers/info). The "quote" upload
 * row ("Parse my quote", icon clip) was removed from the list (2026-10-15);
 * its upload/paste handling below stays wired so the row can return as one
 * entry — uploads go through the composer's paperclip meanwhile. */
const STARTER_PROMPTS: {
  key: string;
  icon: "clip" | "tile" | "layers" | "info";
}[] = [
  { key: "terms", icon: "tile" },
  { key: "budget", icon: "layers" },
  { key: "order", icon: "info" },
];

/** Empty-state entrance: container staggers its children in. Gated on
 * `prefers-reduced-motion` at the call site. */
const containerVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.045, delayChildren: 0.03 } },
};
const itemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.28, ease: "easeOut" } },
};

/** Subtle fade-rise for a freshly-arrived turn. Only applied to new
 * local/streaming turns (not persisted history) and only when motion is
 * allowed — returns no motion props otherwise. */
function bubbleMotion(animate: boolean): Record<string, unknown> {
  return animate
    ? {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.22, ease: "easeOut" },
      }
    : {};
}

/**
 * A docked round's anchor while it holds: the round, the scrollTop it set, and
 * the observer that re-pins it when the band changes.
 */
interface AnswerAnchor {
  roundId: string;
  top: number;
  observer: ResizeObserver | null;
}

type AnchorRef = { current: AnswerAnchor | null };

/**
 * Pin `scroller` to the end of the docking turn's text (`answer-anchor.ts`) and
 * hold it there. Pins to the very bottom first; a last line already in view
 * stays at the bottom.
 *
 * While the anchor holds:
 * - One ResizeObserver per dock watches the scroller and its content wrapper.
 *   The band changes while a round is docked — «Свой ответ» grows from 44 to
 *   120 px, the next question is taller, the viewport is resized or rotated —
 *   and each change re-pins, so the line never ends up under the card.
 * - The browser's own scroll anchoring is off on the scroller: Chrome moved
 *   the anchored position by a pixel one frame after the dock (fractional band
 *   edges at 390×844, e2e frame trace).
 * - `data-anchor-held` is on the scroller (a test hook).
 */
function holdAnchor(scroller: HTMLElement, ref: AnchorRef, roundId: string): void {
  scroller.scrollTop = scroller.scrollHeight;
  const top = measureAnswerEndScrollTop(scroller);
  if (top !== null) scroller.scrollTop = top;
  const held = ref.current;
  let observer = held !== null && held.roundId === roundId ? held.observer : null;
  if (held !== null && held.roundId !== roundId) held.observer?.disconnect();
  if (observer === null && typeof ResizeObserver !== "undefined") {
    const next = new ResizeObserver(() => {
      // Only while this dock's anchor still holds; a release disconnects.
      if (ref.current?.observer === next) holdAnchor(scroller, ref, roundId);
    });
    next.observe(scroller);
    const content = scroller.firstElementChild;
    if (content !== null) next.observe(content);
    observer = next;
  }
  ref.current = { roundId, top: scroller.scrollTop, observer };
  scroller.style.overflowAnchor = "none";
  scroller.setAttribute("data-anchor-held", "");
}

/** End the anchor: the user scrolled, the round undocked, or the list unmounts. */
function releaseAnchor(scroller: HTMLElement | null, ref: AnchorRef): void {
  ref.current?.observer?.disconnect();
  ref.current = null;
  if (scroller === null) return;
  scroller.style.overflowAnchor = "";
  scroller.removeAttribute("data-anchor-held");
}

/**
 * Partition a persisted `cards_json` bag into seller-match cards, product
 * cards, and (at most one) materials-table entry. The `{kind:"materials"}`
 * entry is routed to `materials` and MUST NOT fall into the product bucket
 * (chat document attachments); the last one wins if several are present.
 */
function partitionCards(
  cards:
    | (ProductCardData | SellerMatchCardData | MaterialsCard)[]
    | null
    | undefined,
): {
  sellerCards: SellerMatchCardData[];
  productCards: ProductCardData[];
  materials: MaterialsEvent | null;
} {
  const sellerCards: SellerMatchCardData[] = [];
  const productCards: ProductCardData[] = [];
  let materials: MaterialsEvent | null = null;
  for (const card of cards ?? []) {
    if (isMaterialsCard(card)) materials = card.quote;
    else if (isSellerMatchCard(card)) sellerCards.push(card);
    else productCards.push(card as ProductCardData);
  }
  return { sellerCards, productCards, materials };
}

/** Guest mode (SCRUM-273). Absent for members. */
export interface MessageListGuestOptions {
  /** Attachments locked for guests: «Разобрать смету» becomes the paste path. */
  attachmentsLocked: boolean;
}

export interface MessageListProps {
  /** Durable thread messages from TanStack Query (existing thread). */
  messages: ThreadMessage[];
  /** In-memory turns for the current session (new chat / just-sent). */
  localTurns: LocalTurn[];
  isStreaming: boolean;
  routingIntent: string | null;
  currentAssistantText: string;
  /** In-flight marketplace product cards for the streaming turn (demo slice). */
  currentProductCards: ProductCardData[];
  /** In-flight quote `seller_match` cards for the streaming turn (design). */
  currentSellerCards: SellerMatchCardData[];
  /** In-flight KB sources for the streaming turn (Phase 2 RAG) — one chip each. */
  currentCitations: CitationEvent[];
  /** In-flight follow-up suggestions for the streaming turn (design). */
  currentFollowups: string[];
  /** The pipeline step the server says is running RIGHT NOW (SCRUM-281,
   * `event: stage`); `null` until the first stage and once content replaced
   * it. Before text it replaces the dots; after text it renders under the
   * answer and the caret hides. */
  currentStage?: StageEvent | null;
  /** When the streaming turn began waiting for its first frame (epoch ms);
   * `null` once any frame arrived. After 4 s of that wait the dots carry
   * «Соединяемся…» (owner-approved 2026-10-13). Absent = never shown. */
  awaitingFirstFrameSince?: number | null;
  /** In-flight sourced quote for the streaming turn — the in-chat materials
   * table (chat document attachments). */
  currentMaterials?: MaterialsEvent | null;
  /** In-flight Recommendation Plan for the streaming turn (Smart
   * Recommendations R1, `event: plan`). */
  currentPlan?: Need[];
  /** The in-flight round (`event: clarify`, contract C8). Content for the
   * streaming turn only; once committed it docks in ClarifyCard (W3). */
  currentClarify?: ClarifyRoundEvent | null;
  /**
   * The docked round's id — any docked round: a lead-in round, a follow-up, a
   * smart-rec round, a re-dock after reload — or null when none is docked.
   * While it is set, the pinned position is the end of the docking turn's
   * text at the bottom of the band, not the very bottom (controller rulings,
   * 2026-10-13; `answer-anchor.ts`).
   */
  answerAnchorRoundId?: string | null;
  streamError: ChatStreamError | null;
  /**
   * Send a starter / follow-up / "ask about this" prompt as a user message.
   * The `origin` tag remains part of the send contract (the backend still
   * guards against a client that posts the quote button's own label — see
   * `orchestrator.py` `is_quote_starter`), but SCRUM-146 means the quote card
   * no longer sends at all, so no caller sets it today.
   */
  onPickStarter: (text: string, origin?: string) => void;
  /**
   * SCRUM-146 — open the composer's file picker. The "Parse my quote" starter
   * calls THIS instead of sending its own label as a message: the send could
   * only ever come back with "I don't see your quote yet — upload one", which
   * the greeting above it and the composer's paperclip already offer.
   */
  onOpenQuoteUpload: () => void;
  /**
   * B1 (SCRUM-177/179 residual) — true while the persisted thread's OWN
   * detail fetch is still pending. Distinct from `isStreaming`, which covers
   * an in-flight ASSISTANT turn: this covers the GET that loads the thread
   * itself. Gates `empty` so a cold reload (or a thread switch) doesn't flash
   * "Hi there" before the real transcript is known.
   */
  threadPending?: boolean;
  /**
   * B1 — set when the thread-detail fetch failed. `"notFound"` covers a
   * deleted/foreign thread id (404) or a non-UUID `?t=` (422) — a Retry can
   * never succeed there, so the caller offers "New chat" instead. `"failed"`
   * covers everything else (network / 5xx) and gets a Retry.
   */
  threadError?: "notFound" | "failed" | null;
  /**
   * W2.6 (ruling P10) — why the open thread is not found, when the caller
   * knows better than "maybe deleted": `"guest-expired"` is a guest
   * conversation whose session was pruned before it could be claimed. The
   * slot then says so — its only message (no toast on top of it).
   */
  threadNotFoundReason?: "guest-expired";
  /** Retry the thread-detail fetch (shown when `threadError === "failed"`). */
  onRetryThread?: () => void;
  /**
   * Start a new chat from the not-found state (shown when
   * `threadError === "notFound"`) — retrying a 404/422 is a dead end, and
   * without an exit the restored `?t=` URL reproduces the error forever.
   */
  onThreadErrorNewChat?: () => void;
  /**
   * SCRUM-177 — older messages exist beyond the loaded window. The endpoint
   * used to serve a fixed 20 with no way to reach past it, so reopening a long
   * thread silently showed only its tail.
   */
  hasEarlierMessages?: boolean;
  /** True while a wider window is in flight. */
  loadingEarlier?: boolean;
  /** Widen the window (no-op when `hasEarlierMessages` is false). */
  onLoadEarlier?: () => void;
  /** Regenerate a completed assistant turn by re-sending its preceding user
   * message (Phase 5). Omitted → no Regenerate action. */
  onRegenerate?: (userText: string) => void;
  /**
   * Whether the terminal-error slot offers its retry (default true). False
   * when a clarifying-question answer failed after its first token (W3.2 fix
   * round): a resend would post the summary as a plain chat message — a second
   * bubble, and a charged message for a guest — and the round is already
   * answered, so a `clarify_answer` retry would 409. The error itself stays.
   */
  streamErrorRetryable?: boolean;
  /**
   * Smart Recommendations R1 plan-chip wiring — passed straight through to
   * every `RecommendationPlan` this list renders (persisted, local and
   * in-flight). Omitted → no plan UI renders (defensive default; every real
   * caller supplies these). The R1/R2 questions are rounds and dock in
   * ClarifyCard (W4); nothing in the list answers a question.
   */
  exploredNeedIds?: ReadonlySet<string>;
  needLoading?: ReadonlySet<string>;
  needErrored?: ReadonlySet<string>;
  onFetchNeed?: (needId: string) => void;
  onSeeAllNeed?: (need: Need) => void;
  /** Open the citation sheet on the desktop (right-side) variant. */
  desktop?: boolean;
  /** Guest mode (SCRUM-273): no member-only queries; the paste path. */
  guest?: MessageListGuestOptions;
  /**
   * C1 — the route is centring this empty chat with the composer below it,
   * so the list takes its content height (it may still shrink and scroll on
   * a short window) instead of filling the pane. `ChatRoute` makes this
   * decision once and puts the spacers around the group on the same answer;
   * the list never re-derives it, or the two could disagree and the pane
   * would neither centre nor dock (fix round 1, M1).
   */
  centred?: boolean;
}

/**
 * Copy-to-clipboard affordance under a completed assistant turn (SCRUM-133).
 * Hidden on the in-flight (streaming) turn — only finished answers are
 * copyable. Shows a transient "Copied" state; degrades silently if the
 * Clipboard API is unavailable (e.g. insecure context).
 */
function CopyButton({ text }: { text: string }): ReactElement {
  const { t } = useTranslation("chat");
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (insecure origin / denied) — no-op.
    }
  };
  return (
    <button
      type="button"
      onClick={() => void onCopy()}
      data-testid="chat-copy"
      aria-label={copied ? t("copy.copied") : t("copy.label")}
      title={copied ? t("copy.copied") : t("copy.label")}
      className="inline-flex h-8 items-center gap-1.5 rounded-[8px] px-2 text-[13.5px] font-medium text-[var(--g-ink-2)] transition-colors duration-150 hover:bg-[var(--g-primary-soft)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
    >
      {/* The glyph morphs in place rather than the label swapping under a
          static icon — the confirmation IS the animation. */}
      <span className="relative inline-flex size-4 items-center justify-center">
        <Check
          {...iconProps("sm")}
          className={[
            "absolute text-[var(--g-success)] transition-[opacity,transform] duration-200 motion-reduce:transition-none",
            copied ? "scale-100 opacity-100" : "scale-50 opacity-0",
          ].join(" ")}
        />
        <Copy
          {...iconProps("sm")}
          className={[
            "absolute transition-[opacity,transform] duration-200 motion-reduce:transition-none",
            copied ? "scale-50 opacity-0" : "scale-100 opacity-100",
          ].join(" ")}
        />
      </span>
      {/* Icon-only below sm (640px): the label + Regenerate's did not fit a
          phone's action row beside the thumbs and the time. */}
      <span className="hidden sm:inline">{copied ? t("copy.copied") : t("copy.label")}</span>
    </button>
  );
}

/**
 * Regenerate affordance under a completed assistant turn (Phase 5) — re-sends
 * the preceding user message to produce a fresh answer.
 */
function RegenerateButton({ onClick }: { onClick: () => void }): ReactElement {
  const { t } = useTranslation("chat");
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="chat-regenerate"
      aria-label={t("regenerate")}
      title={t("regenerate")}
      className="group/regen inline-flex h-8 items-center gap-1.5 rounded-[8px] px-2 text-[13.5px] font-medium text-[var(--g-ink-2)] transition-colors duration-150 hover:bg-[var(--g-primary-soft)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
    >
      <RefreshCw
        {...iconProps("sm")}
        className="transition-transform duration-300 group-hover/regen:rotate-180 motion-reduce:transition-none motion-reduce:group-hover/regen:rotate-0"
      />
      <span className="hidden sm:inline">{t("regenerate")}</span>
    </button>
  );
}

/**
 * The assistant's mark, sitting at the head of a turn's left margin.
 *
 * Squared rather than a disc: every avatar in every chat product is a circle,
 * and the turn below it is no longer a balloon either. Desktop-only — at 390px
 * the 44px it costs is worth more as text measure, and alignment alone already
 * separates the two speakers.
 */
function AssistantAvatar({
  design = false,
}: {
  design?: boolean;
}): ReactElement {
  if (design) {
    return (
      <div
        aria-hidden="true"
        data-testid="assistant-avatar"
        className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-[10px]"
        style={{ backgroundColor: "var(--gd-primary)" }}
      >
        {/* The design's mark is a 10px square turned 45° — the shyrdak
            lattice's own unit, drawn once at the head of the turn, rather
            than the sparkle every other assistant wears. */}
        <span
          className="size-[10px] rotate-45"
          style={{ backgroundColor: "var(--gd-on-primary)" }}
        />
      </div>
    );
  }
  return (
    <div
      aria-hidden="true"
      data-testid="assistant-avatar"
      className="mt-1 hidden size-8 shrink-0 items-center justify-center rounded-[10px] border border-[var(--g-rule)] bg-[var(--g-surface)] text-primary md:flex"
    >
      <Icon name="sparkles" size={16} />
    </div>
  );
}

/**
 * The transcript's day marker (spec §4.3) — a centred pill, drawn only inside
 * the design frame.
 *
 * It reads from `created_at`, which every persisted message carries. Local
 * turns have no server timestamp and are, by construction, from this session,
 * so the block of them is introduced by "today" and nothing is inferred about
 * a day the client cannot know.
 */
function DayDivider({ label }: { label: string }): ReactElement {
  return (
    <div className="flex justify-center py-1" data-testid="day-divider">
      <span
        className="rounded-full px-[14px] py-[5px] text-[13px]"
        style={{
          backgroundColor: "var(--gd-rule-5)",
          color: "var(--gd-ink-2)",
          fontFamily: "var(--gd-text)",
        }}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * The filename the estimate card names as its source — taken from the USER
 * turn that carried the document, which is where the only filename in the
 * conversation lives (`MaterialsEvent` has no filename field; see the backend
 * capabilities spec). Undefined when that turn had no attachment or no name,
 * and the card then shows its line count alone rather than a placeholder.
 */
function attachmentNameOf(
  turn: { attachments?: MessageAttachment[] | null } | undefined,
): string | undefined {
  const name = turn?.attachments?.[0]?.filename;
  return name != null && name.length > 0 ? name : undefined;
}

/**
 * Is the persisted user row after `asker` a round's answer summary (contract
 * C12; final fix wave, I3)? It is when `asker` asked an ordinary or quote
 * round that is now answered or skipped — those are answered through a
 * `clarify_answer` turn whose user row is the summary. The answer after it
 * offers no Regenerate: re-sending the summary would post it as a plain chat
 * message. A dismissed round's next user row is a message the user typed.
 */
function summarisesRound(asker: ThreadMessage | undefined): boolean {
  const round = asker?.role === "assistant" ? asker.clarify_round : null;
  if (round === undefined || round === null) return false;
  return (
    (round.kind === "clarify" || round.kind === "quote") &&
    (round.status === "answered" || round.status === "skipped")
  );
}

/** Local-day identity for a timestamp; empty string when it cannot be read. */
function dayKeyOf(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * "Today", or the day itself — `dateLocaleFor` because Chromium has no `ky`
 * ICU data and would otherwise label a Kyrgyz transcript in English (the same
 * gap the thread rail documents).
 */
/** Did the persisted transcript already reach today? */
function endsToday(messages: ThreadMessage[]): boolean {
  const last = messages.at(-1);
  if (last === undefined) return false;
  return dayKeyOf(last.created_at) === dayKeyOf(new Date().toISOString());
}

function dayLabelOf(iso: string, language: string, todayLabel: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  if (dayKeyOf(iso) === dayKeyOf(new Date().toISOString())) return todayLabel;
  return new Date(ms).toLocaleDateString(dateLocaleFor(language), {
    day: "numeric",
    month: "long",
  });
}

/**
 * The user's turn — what was asked across the counter.
 *
 * Still right-aligned (alignment is what makes a transcript scannable), but the
 * asymmetric 18px/6px tail is gone. That tail is the balloon silhouette, and a
 * balloon is the thing this surface is not; on the system's own `--g-radius` it
 * reads as an order line docked to the right margin instead.
 */
/**
 * The message time (SCRUM-222).
 *
 * Muted, small, and outside the bubble so it never competes with the message.
 * Renders NOTHING when the timestamp is missing or unparseable — an absent
 * stamp is invisible, a broken one is a defect the reader has to look at.
 *
 * `title` carries the full value for anyone who needs the date as well; the
 * day itself is stated once by `DayDivider` rather than on every bubble.
 */
function MessageTime({
  iso,
  align = "start",
}: {
  iso?: string;
  align?: "start" | "end";
}): ReactElement | null {
  const { i18n } = useTranslation("chat");
  const label = formatMessageTime(iso, i18n.language);
  if (label === "") return null;
  return (
    <time
      dateTime={iso}
      title={iso}
      data-testid="chat-message-time"
      className={
        "px-1 text-[12px] leading-none text-muted-foreground " +
        (align === "end" ? "self-end" : "self-start")
      }
    >
      {label}
    </time>
  );
}

function UserBubble({
  content,
  attachments,
  animate = false,
  design = false,
  createdAt,
}: {
  content: string;
  attachments?: MessageAttachment[] | null;
  animate?: boolean;
  design?: boolean;
  /** ISO-8601; absent on a turn with no timestamp, which renders none. */
  createdAt?: string;
}): ReactElement | null {
  const hasAttachments = attachments != null && attachments.length > 0;
  // A user turn with NOTHING to show renders nothing at all — not an empty
  // wrapper, not its bottom margin, not a timestamp.
  //
  // A "You might also need" chip tap is not something the user typed: the
  // client sends an empty message (`sendNeedFetch("")`) and the server
  // persists it as a blank `role='user'` row, so every chip tap puts one of
  // these in the transcript. QA's retest thread holds six.
  //
  // It went unnoticed while the duplicated chip reply (SCRUM-188) was the loud
  // symptom, and became visible the moment that was fixed. SCRUM-222 then made
  // it worse: `MessageTime` was added below the content conditional, so a blank
  // row rendered a bare floating timestamp with no bubble under it. Together
  // that is the "messages splitting" QA reported on the SCRUM-188 retest.
  //
  // Guarded here rather than only at the timestamp because the wrapper's own
  // margin is a visible gap too, and because the blank rows already written to
  // the database cannot be un-persisted. An ATTACHMENT-only turn (a photo with
  // no caption) is a real turn and must survive this — hence `hasAttachments`.
  if (!hasAttachments && content.length === 0) return null;
  return (
    <motion.div
      className={
        "flex flex-col items-end gap-1.5 " + (design ? "mb-[22px]" : "mb-5")
      }
      data-testid="chat-message-user"
      {...bubbleMotion(animate)}
    >
      {hasAttachments ? (
        <div className="max-w-[86%] md:max-w-[76%]">
          <MessageAttachments attachments={attachments} />
        </div>
      ) : null}
      {/* An attachment-only turn (no typed text) renders just the chips. */}
      {content.length > 0 ? (
        design ? (
          /* Spec §4.3 restores the balloon this surface had deliberately
             dropped — max-width 70%, `18 18 4 18`, --gd-primary. Where the
             design and the incumbent disagree on appearance the design wins;
             below 1200px the un-tailed block below still renders. */
          <div
            className="max-w-[70%] whitespace-pre-wrap px-[18px] py-[14px] text-[16px] leading-[1.55]"
            style={{
              backgroundColor: "var(--gd-primary)",
              color: "var(--gd-on-primary)",
              borderRadius: "18px 18px 4px 18px",
              fontFamily: "var(--gd-text)",
            }}
          >
            {content}
          </div>
        ) : (
          <div className="max-w-[86%] whitespace-pre-wrap rounded-[var(--g-radius)] bg-primary px-4 py-2.5 text-[15.5px] leading-[1.45] text-primary-foreground md:max-w-[76%]">
            {content}
          </div>
        )
      ) : null}
      <MessageTime iso={createdAt} align="end" />
    </motion.div>
  );
}

/**
 * A full assistant turn: the sparkles avatar + the surface bubble, plus (below
 * the bubble) the seller cards, product cards, citation chip, and follow-ups.
 * The chip + follow-ups sit OUTSIDE the bubble (design: the bubble holds the
 * text; the affordances hang under it).
 */
function AssistantTurn({
  children,
  sellerCards,
  productCards,
  materials,
  citations,
  followups,
  plan,
  planWiring,
  onPickFollowup,
  desktop,
  animate = false,
  copyText,
  messageId,
  regenerateText,
  onRegenerate,
  design = false,
  sourceLabel,
  createdAt,
}: {
  children: ReactNode;
  sellerCards?: SellerMatchCardData[];
  productCards?: ProductCardData[];
  materials?: MaterialsEvent | null;
  /** The REAL KB sources the answer is grounded in (Phase 2). One chip per
   * source; empty/absent = ungrounded (no chip shown, honestly). */
  citations?: CitationEvent[];
  followups?: string[];
  /** This turn's Recommendation Plan (Smart Recommendations R1) — mutually
   * exclusive with `followups` (the backend never emits both). */
  plan?: Need[];
  /** Shared plan-chip wiring (explored/loading/errored sets + callbacks) —
   * threaded through from `MessageListProps` unchanged for every turn. */
  planWiring?: PlanWiring;
  onPickFollowup: (text: string) => void;
  desktop?: boolean;
  animate?: boolean;
  /** Raw assistant text to copy; when set, a Copy button hangs under the
   * bubble. Omitted for the in-flight (streaming) turn (SCRUM-133). */
  copyText?: string;
  /** Assistant message id — when set, the thumbs feedback control shows
   * (completed turns only; Phase 5). */
  messageId?: string;
  /** ISO-8601 for the message time (SCRUM-222); absent renders none. */
  createdAt?: string;
  /** The preceding user message — when set with `onRegenerate`, a Regenerate
   * action re-sends it (completed turns only; Phase 5). */
  regenerateText?: string;
  onRegenerate?: (userText: string) => void;
  /** Render the imported design's turn (spec §4.3) instead of today's. */
  design?: boolean;
  /** The source document's filename, for the estimate card's meta line. */
  sourceLabel?: string;
}): ReactElement {
  // A completed turn's text is what a docked round anchors to
  // (`answer-anchor.ts`); the in-flight turn has no `copyText` and no mark.
  const answerText = copyText !== undefined ? { "data-testid": ANSWER_TEXT_TEST_ID } : {};
  return (
    <motion.div
      className={
        "group/turn flex items-start " +
        (design ? "mb-[22px] gap-3" : "mb-7 gap-3.5")
      }
      data-testid="chat-message-assistant"
      {...bubbleMotion(animate)}
    >
      <AssistantAvatar design={design} />
      {/*
       * NO BUBBLE.
       *
       * The answer used to sit in a rounded, bordered, shadowed card — which
       * made a 500-word grounded answer about tile adhesive read as a chat
       * balloon and capped its measure at whatever the balloon allowed. Un-
       * bubbled, the assistant's turn is a passage on the page: full measure
       * (~68ch, inside the 65-75 comfortable band), real leading, and the
       * hierarchy inside the markdown finally has room to do its job. The user's
       * right-aligned tinted block is what separates the two speakers, so the
       * container here can carry nothing at all.
       */}
      <div className={"min-w-0 flex-1" + (design ? "" : " md:max-w-[62ch]")}>
        {design ? (
          /* Spec §4.3 puts the answer back in a bubble: --gd-surface, 1px
             --gd-rule, `4 18 18 18`. Its measure is the design's 780px
             column rather than the 62ch this surface reads at elsewhere —
             the cards that hang below want the full width. */
          <div
            {...answerText}
            className="px-[18px] py-[14px] text-[16px] leading-[1.55]"
            style={{
              backgroundColor: "var(--gd-surface)",
              border: "1px solid var(--gd-rule)",
              borderRadius: "4px 18px 18px 18px",
              color: "var(--gd-ink-body)",
              fontFamily: "var(--gd-text)",
            }}
          >
            {children}
          </div>
        ) : (
          <div {...answerText} className="text-[16px] leading-[1.62] text-foreground">
            {children}
          </div>
        )}
        {/* Inline KB source chips — one per retrieved source (Phase 2 RAG);
            nothing at all when the answer was ungrounded (honest). */}
        {citations && citations.length > 0 ? (
          <div
            className="mt-3 flex flex-wrap items-center gap-1.5"
            data-testid="chat-citations"
          >
            {citations.map((c) => (
              <CitationChip
                key={c.slug}
                citation={c}
                label={c.title}
                desktop={desktop}
                onAsk={onPickFollowup}
              />
            ))}
          </div>
        ) : null}
        {sellerCards && sellerCards.length > 0 ? (
          <SellerCardsRow cards={sellerCards} />
        ) : null}
        {/* Marketplace product carousel — the payload of a grounded answer.
            Task 5 built the design's card as a SEPARATE EXPORT rather than a
            boolean prop, so opting in means writing its name; this is the
            call site that does it, under the same gate as everything else on
            this surface. Mounting it ungated would put a cream `--gd-*` card
            inside a dark-theme thread at every viewport. */}
        {productCards && productCards.length > 0 ? (
          <div className="mt-4">
            {design ? (
              <ChatProductCardsDesign cards={productCards} />
            ) : (
              <ChatProductCards cards={productCards} />
            )}
          </div>
        ) : null}
        {/* In-chat materials → seller-matches table (chat document
            attachments) — hangs below the bubble like the product carousel.
            Inside the design frame this is the estimate-check card, the
            design's centrepiece (spec §4.4); below 1200px the incumbent
            table renders, because the card's columns collide there. */}
        {materials ? (
          design ? (
            <div className="mt-4 flex flex-col gap-3">
              <EstimateCheckCard
                materials={materials}
                {...(sourceLabel !== undefined ? { sourceLabel } : {})}
              />
              {/*
               * Decision D5: one calculator per material line that carries a
               * backend `calculator` payload — which is NO line today, so
               * nothing renders. The card computes only what it was handed;
               * the mount point exists so the capability lands as data, not
               * as a frontend release.
               */}
              {materials.materials
                .filter((line) => line.calculator != null)
                .map((line) => (
                  <QuantityCalculatorCard
                    key={`calc-${line.id}`}
                    spec={line.calculator ?? null}
                  />
                ))}
            </div>
          ) : (
            <ChatMaterialsTable materials={materials} />
          )
        ) : null}
        {/* Smart Recommendations R1: the plan chip row is THE single
            suggestion surface on a plan turn — the backend never emits
            `followups` alongside `plan`, so this never renders alongside the
            FollowupPills block below. */}
        {plan && plan.length > 0 && planWiring ? (
          <RecommendationPlan
            needs={plan}
            explored={planWiring.explored}
            loading={planWiring.loading}
            errored={planWiring.errored}
            onFetchNeed={planWiring.onFetchNeed}
            onSeeAllNeed={planWiring.onSeeAllNeed}
          />
        ) : null}
        {followups && followups.length > 0 ? (
          <FollowupPills items={followups} onPick={onPickFollowup} />
        ) : null}
        {/* Action row under a COMPLETED turn: copy · feedback · regenerate
            (Phase 5). Omitted for the in-flight streaming turn (no copyText). */}
        {copyText !== undefined && copyText.length > 0 ? (
          /* The turn's footer rule. A drawn hairline separates the answer from
             its controls the way a slip separates the goods from the stamp —
             and it stops copy/feedback/regenerate from floating ambiguously
             between this turn and the next. Revealed on hover/focus-within at
             desktop so a settled transcript stays quiet; always visible on
             touch, where there is no hover to reveal it. */
          <div
            className="mt-3 flex items-center gap-0.5 border-t border-[var(--g-hairline)] pt-2"
            data-testid="chat-message-actions"
          >
            {/* The CONTROLS stay hover-revealed at desktop so a settled
                transcript stays quiet; they are always visible on touch, where
                there is no hover to reveal them. */}
            <div className="flex items-center gap-0.5 opacity-100 transition-opacity duration-200 md:opacity-0 md:group-hover/turn:opacity-100 md:group-focus-within/turn:opacity-100 motion-reduce:transition-none">
              <CopyButton text={copyText} />
              {messageId ? <MessageFeedback messageId={messageId} /> : null}
              {regenerateText && onRegenerate ? (
                <RegenerateButton onClick={() => onRegenerate(regenerateText)} />
              ) : null}
            </div>
            {/* SCRUM-222 — the TIME is not a control and must not be hover-gated.
                It rode the row above until a live check on QA measured 32 of 59
                timestamps invisible at 1280px (all the assistant ones) while all
                59 showed at 390px — i.e. the ticket was met on mobile and silently
                unmet on desktop. It shares the footer rule for alignment, outside
                the opacity wrapper. */}
            <MessageTime iso={createdAt} />
          </div>
        ) : null}
        {/* A turn with no action row (the in-flight one) still gets its time. */}
        {copyText === undefined || copyText.length === 0 ? (
          <div className="mt-2 flex">
            <MessageTime iso={createdAt} />
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}

/**
 * Smart Recommendations R1 — the plan-chip wiring every `AssistantTurn` needs,
 * computed ONCE per `MessageList` render and threaded through as one prop.
 * `undefined` (no wiring supplied) → no plan UI renders, even if a turn
 * carries `plan` data (defensive: every real `ChatRoute` caller supplies this).
 */
interface PlanWiring {
  explored: ReadonlySet<string>;
  loading: ReadonlySet<string>;
  errored: ReadonlySet<string>;
  onFetchNeed: (needId: string) => void;
  onSeeAllNeed: (need: Need) => void;
}

/**
 * Is the open conversation empty: nothing persisted, nothing local, nothing
 * streaming or failed, and the thread not still loading or unreachable? This
 * is the test the empty state renders on. It is gated on the thread-detail
 * fetch's own state too (SCRUM-177/179 residual), so a cold reload or a fetch
 * failure never flashes the greeting while the real transcript is pending.
 * Exported because `ChatRoute` centres the composer on the very same answer
 * (C1, ui-review.md §4.4); two copies of the rule would drift apart.
 */
export function isConversationEmpty({
  messageCount,
  localTurnCount,
  isStreaming,
  streamError,
  threadPending = false,
  threadError = null,
}: {
  messageCount: number;
  localTurnCount: number;
  isStreaming: boolean;
  streamError: ChatStreamError | null;
  threadPending?: boolean;
  threadError?: "notFound" | "failed" | null;
}): boolean {
  return (
    messageCount === 0 &&
    localTurnCount === 0 &&
    !isStreaming &&
    streamError === null &&
    !threadPending &&
    threadError === null
  );
}

export function MessageList({
  messages,
  localTurns,
  isStreaming,
  routingIntent,
  currentAssistantText,
  currentProductCards,
  currentSellerCards,
  currentCitations,
  currentFollowups,
  currentStage = null,
  awaitingFirstFrameSince = null,
  currentMaterials = null,
  currentPlan = [],
  currentClarify = null,
  answerAnchorRoundId = null,
  streamError,
  onPickStarter,
  onOpenQuoteUpload,
  threadPending = false,
  threadError = null,
  threadNotFoundReason,
  onRetryThread,
  onThreadErrorNewChat,
  hasEarlierMessages = false,
  loadingEarlier = false,
  onLoadEarlier,
  onRegenerate,
  streamErrorRetryable = true,
  exploredNeedIds,
  needLoading,
  needErrored,
  onFetchNeed,
  onSeeAllNeed,
  desktop = false,
  guest,
  centred = false,
}: MessageListProps): ReactElement {
  const { t, i18n } = useTranslation("chat");
  /*
   * The imported design's transcript, on the same gate the frame and the rail
   * use — ≥1200px and light theme. Read here rather than taken as a prop:
   * `desktop` already comes down from `ChatRoute` and means something else
   * (768px, which drives sheet-vs-panel behaviour), and two booleans named
   * for two different thresholds would be a standing invitation to use the
   * wrong one.
   */
  const design = useAssistantDesign();
  // Smart Recommendations R1: one wiring object shared by every AssistantTurn
  // (persisted, local, and in-flight) — undefined when the caller omits the
  // plan-chip callbacks (no plan UI renders at all in that case).
  const planWiring: PlanWiring | undefined =
    onFetchNeed && onSeeAllNeed
      ? {
          explored: exploredNeedIds ?? EMPTY_NEED_SET,
          loading: needLoading ?? EMPTY_NEED_SET,
          errored: needErrored ?? EMPTY_NEED_SET,
          onFetchNeed,
          onSeeAllNeed,
        }
      : undefined;
  const prefersReduced = useReducedMotion() ?? false;
  const animateNew = !prefersReduced;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Whether the user is pinned to the bottom; updated on scroll. Default true
  // so the first turn scrolls into view.
  const pinnedToBottom = useRef(true);
  // Drives the floating "jump to latest" affordance — shown only when the user
  // has scrolled up off the bottom.
  const [showJump, setShowJump] = useState(false);

  /*
   * A docked round's anchor (controller rulings, 2026-10-13;
   * `answer-anchor.ts`). Never while an error slot shows under the turn: the
   * bottom keeps the error in view. `anchorRef` is the anchor while it holds
   * (`holdAnchor`); any scroll of the user's own releases it. `anchorKeyRef`
   * is the key the pin effect last saw, so a dock is told apart from a
   * re-render of the same dock.
   */
  const anchorKey = streamError === null ? answerAnchorRoundId : null;
  const anchorRef = useRef<AnswerAnchor | null>(null);
  const anchorKeyRef = useRef<string | null>(null);
  // The anchor's observer must not outlive the list.
  useEffect(() => () => releaseAnchor(null, anchorRef), []);

  const isAtBottom = (el: HTMLDivElement): boolean =>
    el.scrollHeight - el.scrollTop - el.clientHeight <
    SCROLL_BOTTOM_THRESHOLD_PX;

  const onScroll = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    // The anchor's own scroll is not the user's: it stays "pinned" (the next
    // turn scrolls to the bottom) and offers no «jump to latest», which would
    // sit on the very lines the anchor brought into view.
    const anchor = anchorRef.current;
    if (anchor !== null && Math.abs(el.scrollTop - anchor.top) <= 1) {
      pinnedToBottom.current = true;
      setShowJump(false);
      return;
    }
    if (anchor !== null) releaseAnchor(el, anchorRef);
    const atBottom = isAtBottom(el);
    pinnedToBottom.current = atBottom;
    setShowJump(!atBottom);
  };

  const jumpToLatest = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    if (anchorRef.current !== null) releaseAnchor(el, anchorRef);
    el.scrollTo({
      top: el.scrollHeight,
      behavior: prefersReduced ? "auto" : "smooth",
    });
    pinnedToBottom.current = true;
    setShowJump(false);
  };

  // Auto-scroll on new content unless the user scrolled up (AC #11). While a
  // round is docked, "pinned" means the end of its turn's text at the bottom
  // of the band (`holdAnchor`), from the dock until the user scrolls
  // themselves; an undocked turn goes back to the very bottom.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const docked = anchorKey !== anchorKeyRef.current;
    anchorKeyRef.current = anchorKey;
    if (!el) return;
    if (
      anchorKey !== null &&
      pinnedToBottom.current &&
      (docked || anchorRef.current?.roundId === anchorKey)
    ) {
      holdAnchor(el, anchorRef, anchorKey);
      setShowJump(false);
      return;
    }
    if (anchorRef.current !== null) releaseAnchor(el, anchorRef);
    if (pinnedToBottom.current) {
      el.scrollTop = el.scrollHeight;
      setShowJump(false);
    }
  }, [
    messages.length,
    localTurns.length,
    currentAssistantText,
    isStreaming,
    currentStage,
    currentMaterials,
    streamError,
    anchorKey,
  ]);

  // A completed turn exists TWICE: once optimistically in `localTurns`, and
  // again in `messages` once ChatRoute's post-`done` invalidation refetches the
  // thread. Both lists render below, so without this the same turn appears
  // twice — the persisted copy first (plain text, since a persisted message
  // carries no clarify/live wiring), then the local copy with its interactive
  // parts.
  //
  // `commitAssistantTurn` stamps the server's `message_id` onto the local turn
  // for exactly this purpose; once the persisted row shows up under that id,
  // the optimistic copy has been superseded and must stand down. Local turns
  // WITHOUT a messageId (an in-flight or never-persisted turn) are always
  // kept — dropping those would lose the turn entirely.
  const persistedIds = useMemo(
    () => new Set(messages.map((m) => m.id)),
    [messages],
  );
  const pendingLocalTurns = useMemo(
    () =>
      localTurns.filter(
        (turn) =>
          turn.messageId === undefined || !persistedIds.has(turn.messageId),
      ),
    [localTurns, persistedIds],
  );

  const empty = isConversationEmpty({
    messageCount: messages.length,
    localTurnCount: pendingLocalTurns.length,
    isStreaming,
    streamError,
    threadPending,
    threadError,
  });
  // C1 (ui-review.md §4.4): when `ChatRoute` centres the empty chat, this list
  // stops filling the pane and takes its content height (`centred`, above).
  // The first message brings back `flex-1`, which docks the composer.

  /**
   * The most recent thing the user actually asked, across BOTH lists — the
   * optimistic local turn wins because it is the newer of the two. This is what
   * a failed stream retries; without it the error state could only tell the user
   * to retype their own question.
   */
  const lastUserText = useMemo((): string | undefined => {
    for (let i = pendingLocalTurns.length - 1; i >= 0; i -= 1) {
      const turn = pendingLocalTurns[i];
      if (turn?.role === "user" && turn.content.length > 0) return turn.content;
    }
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message?.role === "user" && message.content.length > 0) {
        return message.content;
      }
    }
    return undefined;
  }, [messages, pendingLocalTurns]);

  // Content already on screen for the in-flight turn. When it is there and no
  // stage is running, the text slot shows NOTHING — the dots would claim work
  // that is not happening (SCRUM-281: every indicator is a real step).
  const inFlightHasContent =
    currentProductCards.length > 0 ||
    currentSellerCards.length > 0 ||
    currentMaterials !== null ||
    currentClarify !== null ||
    currentPlan.length > 0 ||
    currentFollowups.length > 0;

  // Split the streaming text into already-revealed head + the newest tail
  // char(s) so only the tail animates (FPS — story risk note).
  const { head, tail } = useMemo(() => {
    if (currentAssistantText.length === 0) {
      return { head: "", tail: "" };
    }
    const splitAt = Math.max(0, currentAssistantText.length - 1);
    return {
      head: currentAssistantText.slice(0, splitAt),
      tail: currentAssistantText.slice(splitAt),
    };
  }, [currentAssistantText]);

  // Reset the pinned flag whenever a brand-new stream starts.
  useEffect(() => {
    if (isStreaming && currentAssistantText.length === 0) {
      pinnedToBottom.current = true;
    }
  }, [isStreaming, currentAssistantText.length]);

  return (
    <div
      className={
        "relative flex min-h-0 flex-col " + (centred ? "flex-initial" : "flex-1")
      }
    >
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className={
          (centred ? "flex-initial" : "flex-1") +
          " overflow-y-auto " +
          (empty
            ? "px-0 pb-2 pt-0"
            : design
              ? // Spec §4.3: the design's own scroll padding, 28/32/12.
                "px-8 pb-3 pt-7"
              : "px-4 pb-2 pt-4")
        }
        data-testid="chat-message-list"
        // Test hooks: the docked round the transcript anchors to, if any;
        // `data-anchor-held` (set by `holdAnchor`) says the anchor holds now.
        data-anchor-round={anchorKey ?? undefined}
      >
        {/* The empty state is a SHEET and wants the pane; a transcript is prose
            and wants a measure. Same scroller, two different jobs, so the
            wrapper widens only when there is nothing to read — except on the
            design frame, where the empty sheet takes the composer's own 780px
            measure so the heading starts where the composer does (C1; it sat
            68px to the left of it). */}
        <div
          className={
            "mx-auto w-full " +
            (empty
              ? design
                ? "max-w-[780px]"
                : "max-w-[980px]"
              : design
                ? "max-w-[780px]"
                : "max-w-[720px]")
          }
        >
          {threadError !== null ? (
            <ThreadLoadErrorSlot
              kind={threadError}
              notFoundReason={threadNotFoundReason}
              onRetry={onRetryThread}
              onNewChat={onThreadErrorNewChat}
            />
          ) : threadPending ? (
            <ThreadLoadingSkeleton prefersReduced={prefersReduced} />
          ) : empty ? (
            <EmptyState
              prefersReduced={prefersReduced}
              onPickStarter={onPickStarter}
              onOpenQuoteUpload={onOpenQuoteUpload}
              guest={guest}
              design={design}
            />
          ) : (
            <div role="log" aria-label={t("messages.log")} aria-live="polite">
              {/* SCRUM-177: reach past the loaded window. Sits at the TOP of
                  the log because that is where the missing history belongs. */}
              {hasEarlierMessages && onLoadEarlier ? (
                <div className="flex justify-center pb-3">
                  <button
                    type="button"
                    onClick={onLoadEarlier}
                    disabled={loadingEarlier}
                    data-testid="chat-load-earlier"
                    className="rounded-full border border-[var(--g-hairline)] bg-[var(--g-surface)] px-4 py-1.5 text-[14px] font-medium text-[var(--g-ink-2)] transition-colors hover:text-foreground disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
                  >
                    {loadingEarlier
                      ? t("history.loadingEarlier")
                      : t("history.loadEarlier")}
                  </button>
                </div>
              ) : null}
              {messages.map((m, i) => {
                const prev = i > 0 ? messages[i - 1] : undefined;
                /* One divider per day boundary, and one before the first
                   message — the design opens the transcript with it. */
                const divider =
                  design &&
                  (prev === undefined ||
                    dayKeyOf(prev.created_at) !== dayKeyOf(m.created_at)) ? (
                    <DayDivider
                      label={dayLabelOf(
                        m.created_at,
                        i18n.language,
                        t("messages.today"),
                      )}
                    />
                  ) : null;

                if (m.role === "user") {
                  return (
                    <Fragment key={m.id}>
                      {divider}
                      <UserBubble
                        content={m.content}
                        attachments={m.attachments}
                        design={design}
                        createdAt={m.created_at}
                      />
                    </Fragment>
                  );
                }
                const { sellerCards, productCards, materials } = partitionCards(
                  m.cards_json,
                );
                const prevUser =
                  prev?.role === "user" && !summarisesRound(messages[i - 2])
                    ? prev.content
                    : undefined;
                return (
                  <Fragment key={m.id}>
                    {divider}
                    <AssistantTurn
                      sellerCards={sellerCards}
                      productCards={productCards}
                      materials={materials}
                      plan={m.plan_json ?? undefined}
                      planWiring={planWiring}
                      onPickFollowup={onPickStarter}
                      desktop={desktop}
                      design={design}
                      sourceLabel={attachmentNameOf(prev)}
                      createdAt={m.created_at}
                      copyText={m.content}
                      messageId={m.id}
                      regenerateText={prevUser}
                      onRegenerate={onRegenerate}
                    >
                      <Markdown content={m.content} />
                    </AssistantTurn>
                  </Fragment>
                );
              })}

              {/*
               * The local block is this session's, so it is today's — the one
               * day the client can state without a server timestamp. Drawn
               * only when the persisted transcript did not already open today
               * (spec §4.3, decision D4).
               */}
              {design &&
              pendingLocalTurns.length > 0 &&
              !endsToday(messages) ? (
                <DayDivider label={t("messages.today")} />
              ) : null}

              {pendingLocalTurns.map((turn, i) => {
                if (turn.role === "user") {
                  return (
                    <UserBubble
                      key={`local-${i}`}
                      content={turn.content}
                      attachments={turn.attachments}
                      animate={animateNew}
                      design={design}
                      createdAt={turn.createdAt}
                    />
                  );
                }
                const prevTurn = i > 0 ? pendingLocalTurns[i - 1] : undefined;
                // An answer turn (its user turn is a round's summary) offers
                // no Regenerate (final fix wave, I3).
                const prevUser =
                  prevTurn?.role === "user" && prevTurn.answersRound === undefined
                    ? prevTurn.content
                    : undefined;
                return (
                  <AssistantTurn
                    key={`local-${i}`}
                    sellerCards={turn.sellerCards}
                    productCards={turn.cards}
                    materials={turn.materials}
                    citations={turn.citations}
                    followups={turn.followups}
                    plan={turn.plan}
                    planWiring={planWiring}
                    onPickFollowup={onPickStarter}
                    desktop={desktop}
                    design={design}
                    sourceLabel={attachmentNameOf(prevTurn)}
                    createdAt={turn.createdAt}
                    animate={animateNew}
                    copyText={turn.content}
                    messageId={turn.messageId}
                    regenerateText={prevUser}
                    onRegenerate={onRegenerate}
                  >
                    <Markdown content={turn.content} />
                  </AssistantTurn>
                );
              })}

              {/* In-flight assistant message + terminal-error slot. */}
              {(isStreaming || streamError !== null) && (
                <AssistantTurn
                  sellerCards={currentSellerCards}
                  productCards={currentProductCards}
                  materials={streamError === null ? currentMaterials : null}
                  citations={
                    streamError === null ? currentCitations : undefined
                  }
                  followups={streamError === null ? currentFollowups : []}
                  plan={streamError === null ? currentPlan : []}
                  planWiring={planWiring}
                  onPickFollowup={onPickStarter}
                  desktop={desktop}
                  design={design}
                  sourceLabel={attachmentNameOf(pendingLocalTurns.at(-1))}
                  animate={animateNew}
                >
                  {streamError !== null ? (
                    <StreamErrorSlot
                      error={streamError}
                      {...(streamErrorRetryable && lastUserText !== undefined && onRegenerate
                        ? { onRetry: () => onRegenerate(lastUserText) }
                        : {})}
                    />
                  ) : currentAssistantText.length === 0 ? (
                    currentStage !== null ? (
                      <StageIndicator
                        stage={currentStage}
                        variant="lead"
                        design={design}
                        intent={routingIntent}
                      />
                    ) : inFlightHasContent ? null : (
                      // The dots, and — after 4 s with no frame at all for
                      // this turn — «Соединяемся…» beside them.
                      <span
                        className="inline-flex items-center"
                        data-testid="chat-awaiting"
                      >
                        <TypingIndicator />
                        <ConnectingHint since={awaitingFirstFrameSince} />
                      </span>
                    )
                  ) : (
                    <>
                      <div data-testid="chat-streaming-text">
                        {/* Writing is finished the moment a trailing stage
                            shows or a content frame has arrived (content
                            frames only ever follow finished text) — render
                            the whole answer as one Markdown block so it
                            never splits mid-emphasis or orphans the last
                            char on its own line while other work runs. */}
                        {currentStage !== null || inFlightHasContent ? (
                          <Markdown content={currentAssistantText} />
                        ) : (
                          <>
                            <Markdown content={head} />
                            <motion.span
                              key={currentAssistantText.length}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.05 }}
                              className="whitespace-pre-wrap"
                            >
                              {tail}
                            </motion.span>
                            {/* The caret means "still writing" — only
                                reachable here, where there is no trailing
                                stage and no content frame yet. */}
                            <span
                              className="g-caret ml-px inline-block text-primary"
                              aria-hidden="true"
                              data-testid="chat-caret"
                            >
                              ▍
                            </span>
                          </>
                        )}
                      </div>
                      {currentStage !== null ? (
                        <StageIndicator
                          stage={currentStage}
                          variant="trailing"
                          design={design}
                          intent={routingIntent}
                        />
                      ) : null}
                    </>
                  )}
                </AssistantTurn>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Jump-to-latest: re-pins to the live answer after the user scrolls up.
          Centred rather than bottom-right, where it sat directly on top of the
          last line of the answer at 390px, and labelled rather than a bare
          chevron disc — the control now says where it goes. It animates in and
          out instead of popping, since it appears mid-scroll. */}
      <AnimatePresence>
        {showJump && (
          <motion.div
            className="pointer-events-none absolute inset-x-0 bottom-3 z-10 flex justify-center"
            initial={prefersReduced ? false : { opacity: 0, y: 8 }}
            animate={prefersReduced ? undefined : { opacity: 1, y: 0 }}
            exit={prefersReduced ? undefined : { opacity: 0, y: 8 }}
            transition={{ duration: MOTION.dur.nav, ease: MOTION.ease.out }}
          >
            <button
              type="button"
              onClick={jumpToLatest}
              title={t("jumpToLatest")}
              data-testid="chat-jump-latest"
              className="pointer-events-auto inline-flex h-10 items-center gap-1.5 rounded-[var(--g-radius-pill)] border border-[var(--g-rule)] bg-[var(--g-elevated)] pl-4 pr-3 text-[14px] font-semibold text-foreground shadow-[var(--g-shadow-lg)] transition-transform duration-150 hover:-translate-y-px active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              {t("jumpToLatest")}
              <ChevronDown {...iconProps("sm")} className="text-primary" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** The opening sheet for a brand-new chat: the heading and the greeting (and,
 * for a guest, the one-line intro) over the order pad — the «Спросите | Что
 * решит» head and the starter rows. One column at every width; the
 * «Что решит» column shows from `sm` (pure CSS, no JS breakpoint). The owner
 * removed the pad on 2026-10-08 and restored it exactly as before on
 * 2026-10-09 (ADR-053). */
function EmptyState({
  prefersReduced,
  onPickStarter,
  onOpenQuoteUpload,
  guest,
  design = false,
}: {
  prefersReduced: boolean;
  onPickStarter: (text: string, origin?: string) => void;
  onOpenQuoteUpload: () => void;
  /** Guest mode (SCRUM-273): no member-only queries; the paste path. */
  guest?: MessageListGuestOptions;
  /**
   * The imported design's frame (≥1200px, light). The sheet then has the
   * composer's measure and no side padding, so the heading's left edge is the
   * composer's (C1).
   */
  design?: boolean;
  /**
   * SCRUM-177 — older messages exist beyond the loaded window. The endpoint
   * used to serve a fixed 20 with no way to reach past it, so reopening a long
   * thread silently showed only its tail.
   */
  hasEarlierMessages?: boolean;
  /** True while a wider window is in flight. */
  loadingEarlier?: boolean;
  /** Widen the window (no-op when `hasEarlierMessages` is false). */
  onLoadEarlier?: () => void;
}): ReactElement {
  const { t } = useTranslation("chat");
  // A0: project-aware cold-start. Read the user's captured project (shared
  // `['projects']` cache with the dashboard) so a starter can reference their
  // real flat ("…on 62 m²…") instead of a hardcoded placeholder. Absent project
  // → the generic starter (static fallback).
  // GET /projects is member-only: for a guest it 401s, and the member
  // refresh-and-redirect would send them to /login (SCRUM-273).
  const { data: projectData } = useQuery({
    queryKey: ["projects"],
    queryFn: listProjects,
    staleTime: 60_000,
    enabled: guest === undefined,
  });
  /** A guest without attachments pastes the estimate instead (spec). */
  const pasteMode = guest?.attachmentsLocked === true;
  // Guard `projects` too: a payload missing the key otherwise throws here and
  // white-screens the whole chat empty state.
  const project = projectData?.projects?.[0];
  const area = project?.area_m2 != null ? parseDecimal(project.area_m2) : 0;
  const budget =
    project?.budget_kgs != null ? parseDecimal(project.budget_kgs) : 0;

  /** The label for one starter — project-aware for the "budget" starter when a
   * project carries an area (and budget), else the generic static label. */
  const starterLabel = (key: string): string => {
    if (key === "budget") {
      if (area > 0 && budget > 0) {
        return t("starter.budgetProject", {
          area: fmtNum(area),
          budget: fmtNum(budget),
        });
      }
      if (area > 0) {
        return t("starter.areaProject", { area: fmtNum(area) });
      }
    }
    return t(`starter.${key}`);
  };
  return (
    <motion.div
      className="pb-4 pt-2"
      data-testid="chat-empty"
      variants={prefersReduced ? undefined : containerVariants}
      initial={prefersReduced ? false : "hidden"}
      animate={prefersReduced ? undefined : "show"}
    >
      {/*
       * The opening is the SHEET ITSELF, not a card on a page.
       *
       * The first pass of this rebuild deleted the sparkle disc and flattened
       * the 2x2 starter grid to 1x4 — and then put the result inside a centred,
       * rounded, bordered white card floating in the pane. That is the assistant
       * template re-skinned, not refused: the contract says "no centred hero",
       * and a rounded container holding a headline and a prose paragraph is the
       * balloon the THESIS names.
       *
       * So there is no container. The pane is one continuous ruled sheet: the
       * motif carries it edge to edge, the heading sits directly on it, and the
       * intent rows are ruled lines across the full width running down into the
       * composer. Nothing floats. (The shyrdak motif that carried it was removed on
       * 2026-10-15: the chat ground is solid white.)
       */}
      <motion.div
        variants={prefersReduced ? undefined : itemVariants}
        className={
          design
            ? "relative overflow-hidden px-0 pb-7 pt-10"
            : "relative overflow-hidden px-5 pb-6 pt-6 md:px-8 md:pb-8 md:pt-10"
        }
      >
        <h2 className="font-display relative max-w-[15ch] text-[29px] font-extrabold leading-[1.06] text-foreground md:text-[39px]">
          {t("empty.heading")}
        </h2>
        <p className="relative mt-3.5 max-w-[46ch] text-[16px] leading-[1.55] text-[var(--g-ink-2)]">
          {pasteMode ? t("guest.greetingPaste") : t("empty.greeting")}
        </p>
        {guest !== undefined ? (
          // The landing moved to /about (SCRUM-275): a first-time visitor at
          // `/` is told what GardEn is in one line, with the way to more.
          <p
            className="relative mt-3 max-w-[52ch] text-[14px] leading-[1.5] text-[var(--g-ink-2)]"
            data-testid="chat-guest-intro"
          >
            {t("guest.intro")}{" "}
            <Link
              to="/about"
              data-testid="chat-guest-about"
              className="rounded-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
            >
              {t("guest.aboutLink")}
            </Link>
          </p>
        ) : null}
      </motion.div>

      {/*
       * The order pad. Full-bleed ruled lines with a DRAWN column head — the
       * armature raise, which the first pass left implied by whitespace on this
       * surface while every other surface drew it. The upload row leads and is
       * typed differently (accent + its own affordance) because it is the only
       * row that opens a file picker rather than sending a question.
       */}
      <div className="flex items-baseline justify-between gap-3 border-y border-[var(--g-rule-strong)] px-5 py-1.5 md:px-8">
        <span className="text-[11.5px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)]">
          {t("empty.colAsk")}
        </span>
        {/* Hidden with the column it labels. The settles value drops below
            `sm` so the question keeps the width; a head left drawn over an
            empty column is a rule pretending to be a grid. */}
        <span className="hidden text-[11.5px] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)] sm:block">
          {t("empty.colSettles")}
        </span>
      </div>
      <div className="g-ruled border-b border-[var(--g-rule)]" role="list">
        {STARTER_PROMPTS.map(({ key, icon }) => {
          const label = starterLabel(key);
          const isUpload = key === "quote";
          return (
            <motion.div
              key={key}
              role="listitem"
              variants={prefersReduced ? undefined : itemVariants}
            >
              <button
                type="button"
                data-testid={`chat-starter-${key}`}
                data-mode={isUpload ? (pasteMode ? "paste" : "upload") : undefined}
                onClick={() => {
                  // SCRUM-146: the quote row is an UPLOAD affordance (note its
                  // paperclip), not a question. Sending its own label could only
                  // ever answer "I don't see your quote yet — upload one".
                  if (isUpload) {
                    onOpenQuoteUpload();
                    return;
                  }
                  onPickStarter(label);
                }}
                className={[
                  "group flex w-full items-center gap-3.5 px-5 py-4 text-left md:px-8",
                  "min-h-[60px] transition-colors duration-150",
                  "hover:bg-[var(--g-primary-soft)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--g-primary)]",
                  isUpload ? "bg-[var(--g-accent-soft)]/40" : "",
                ].join(" ")}
              >
                <span
                  aria-hidden="true"
                  className={[
                    "shrink-0",
                    isUpload ? "text-accent" : "text-[var(--g-ink-3)]",
                  ].join(" ")}
                >
                  {/* A bare glyph, not an icon chip. The icon is meaningful
                      (clip = upload, tile = compare, layers = quantity,
                      info = advice); the tinted rounded square it used to sit
                      in was the card template's calling card. */}
                  {isUpload ? (
                    pasteMode ? (
                      <ClipboardPaste {...iconProps("md")} />
                    ) : (
                      <Paperclip {...iconProps("md")} />
                    )
                  ) : (
                    <Icon name={icon} size={18} />
                  )}
                </span>
                <span className="min-w-0 flex-1 text-[15.5px] font-medium leading-snug text-foreground">
                  {label}
                </span>
                {/* The right column the head names: what this row SETTLES.
                    A column head with nothing under it is a rule pretending to
                    be a grid. Hidden below `sm` where the label needs the
                    width more than the grid needs its second column. */}
                <span className="hidden shrink-0 text-[13px] text-[var(--g-ink-2)] sm:block">
                  {t(`empty.settles.${key}`)}
                </span>
                <span
                  aria-hidden="true"
                  className="shrink-0 text-[var(--g-ink-2)] transition-[transform,color] duration-150 group-hover:translate-x-0.5 group-hover:text-primary motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
                >
                  <ArrowUpRight {...iconProps("sm")} />
                </span>
              </button>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

/**
 * B1 (SCRUM-177/179 residual) — the persisted thread itself failed to load.
 *
 * Reuses `StreamErrorSlot`'s exact visual anatomy below (icon + message +
 * action button, `--g-error` color-mix border/background, `--g-radius`) as a
 * sibling block rather than by calling that component directly: its `error`
 * prop is typed to the streaming `ChatStreamError` taxonomy, which this
 * thread-load failure isn't a member of, and the recovery action differs per
 * case here (Retry vs. New chat) rather than switching on an error code.
 *
 * DESIGN-SYSTEM CALLOUT (GUARDRAILS rule 10 / DESIGN-SYSTEM.md step 5):
 * DESIGN-SYSTEM.md screen 8 (Chat) enumerates "empty with 4 starter prompts,
 * streaming reveal, citation chips, follow-ups, error toast, rate-limit
 * message" — it defines NO thread-rehydration failure state, and
 * `docs/GardEn/src/screens/Chat.jsx` draws none. This state is therefore
 * DRAFTED consistent with the system (verbatim `StreamErrorSlot` anatomy),
 * not copied from an approved screen — a candidate for a future
 * design-system addition, not silently canon.
 */
function ThreadLoadErrorSlot({
  kind,
  notFoundReason,
  onRetry,
  onNewChat,
}: {
  kind: "notFound" | "failed";
  /** W2.6 (P10): a guest chat pruned before its claim says so, not "deleted". */
  notFoundReason?: "guest-expired";
  onRetry?: () => void;
  onNewChat?: () => void;
}): ReactElement {
  const { t } = useTranslation("chat");
  const message =
    kind === "failed"
      ? t("errors.threadLoad")
      : notFoundReason === "guest-expired"
        ? t("guest.threadExpired")
        : t("errors.threadNotFound");
  return (
    <div
      role="alert"
      data-testid="chat-transcript-load-error"
      className="flex flex-col gap-2.5 rounded-[var(--g-radius)] border border-[color-mix(in_srgb,var(--g-error)_30%,transparent)] bg-[color-mix(in_srgb,var(--g-error)_7%,transparent)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <span className="flex items-start gap-2.5 text-[15px] leading-snug text-foreground">
        <span className="mt-px shrink-0 text-[var(--g-error)]">
          <RotateCw {...iconProps("sm")} />
        </span>
        <span>{message}</span>
      </span>
      {kind === "failed" ? (
        <button
          type="button"
          onClick={onRetry}
          data-testid="chat-transcript-retry"
          className="inline-flex h-9 shrink-0 items-center gap-1.5 self-start rounded-[10px] bg-primary px-3.5 text-[14px] font-semibold text-[var(--g-on-primary)] transition-transform duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none sm:self-auto"
        >
          <RotateCw {...iconProps("sm")} />
          {t("errors.retry")}
        </button>
      ) : (
        <button
          type="button"
          onClick={onNewChat}
          data-testid="chat-transcript-newchat"
          className="inline-flex h-9 shrink-0 items-center gap-1.5 self-start rounded-[10px] bg-primary px-3.5 text-[14px] font-semibold text-[var(--g-on-primary)] transition-transform duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none sm:self-auto"
        >
          {t("newChat")}
        </button>
      )}
    </div>
  );
}

/**
 * B1 (SCRUM-177/179 residual) — shown while the persisted thread's own
 * detail fetch is still pending. Three placeholder bubble rows, alternating
 * left/right like a real transcript; `animate-pulse` guarded by the same
 * `prefersReduced` flag every other motion in this file respects (static
 * bars under reduced motion). Uses only `--g-surface` / `--g-hairline` /
 * `--g-radius` — no new colours. Same DESIGN-SYSTEM.md drafted-state
 * disclosure as `ThreadLoadErrorSlot` above applies here too.
 */
function ThreadLoadingSkeleton({
  prefersReduced,
}: {
  prefersReduced: boolean;
}): ReactElement {
  const { t } = useTranslation("chat");
  return (
    <div
      aria-busy="true"
      aria-label={t("history.loadingThread")}
      data-testid="chat-transcript-loading"
      className="flex flex-col gap-3 py-2"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={"flex " + (i % 2 === 0 ? "justify-start" : "justify-end")}
        >
          <div
            className={
              "h-10 w-2/5 rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] " +
              (prefersReduced ? "" : "animate-pulse")
            }
          />
        </div>
      ))}
    </div>
  );
}

/**
 * A terminal stream failure.
 *
 * Was a bare red sentence with no way out: the user had to work out for
 * themselves that re-typing the question was the recovery. Now it states the
 * problem and offers the action, which is the whole difference between an error
 * message and an error STATE. `rate_limited_user`/`rate_limited_ip` and
 * `budget_exhausted` are the codes where retrying immediately is genuinely
 * wrong, so they get no retry — an action that cannot work is worse than none.
 *
 * This is also the ONLY surface for a rate-limit event (bug-wave
 * 2026-09-02): `ChatRoute`'s toast used to fire for it too, duplicating the
 * exact same sentence in two places for one failure (evidenced on QA — a
 * tester saw four orphaned bubbles, this slot, AND a toast all repeating
 * "not so fast"). The slot wins because it sits where the user is already
 * looking and, unlike a toast, does not auto-dismiss while the wait is still
 * running.
 */
function StreamErrorSlot({
  error,
  onRetry,
}: {
  error: ChatStreamError;
  onRetry?: () => void;
}): ReactElement {
  const { t } = useTranslation("chat");
  let message: string;
  let retryable = true;
  switch (error.code) {
    case "not_implemented_in_mvp_sprint": {
      const intentLabel =
        error.intent !== undefined
          ? t(`intent.${error.intent}`, { defaultValue: error.intent })
          : "";
      message = t("errors.notImplemented", { intent: intentLabel });
      retryable = false;
      break;
    }
    case "budget_exhausted":
      message = t("errors.budgetExhausted");
      retryable = false;
      break;
    case "rate_limited_user":
      // Truthful for THIS case: the caller's own per-user budget is what's
      // exhausted, so "you" is the correct subject.
      message =
        error.retryAfterSeconds !== undefined
          ? t("errors.rateLimited", { seconds: error.retryAfterSeconds })
          : t("errors.rateLimitedNoSeconds");
      retryable = false;
      break;
    case "rate_limited_ip":
      // The per-IP bucket tripped, shared with everyone on the caller's
      // network — "not so fast" would blame the wrong party here (bug-wave
      // 2026-09-02 QA evidence: reporter was at 180/200 on their OWN hourly
      // budget). Names the shared network, never who else is on it
      // (REQ-TRD-§14.6-A).
      message =
        error.retryAfterSeconds !== undefined
          ? t("errors.rateLimitedIp", { seconds: error.retryAfterSeconds })
          : t("errors.rateLimitedIpNoSeconds");
      retryable = false;
      break;
    case "session_expired":
      message = t("errors.sessionExpired");
      retryable = false;
      break;
    case "network_error":
      message = t("errors.connectionLost");
      break;
    case "guest_unavailable":
      message = t("guest.errors.unavailable");
      break;
    case "guest_ip_cap":
      message = t("guest.errors.ipCap");
      retryable = false;
      break;
    case "guest_attachments_locked":
      message = t("guest.errors.attachmentsLocked");
      retryable = false;
      break;
    case "guest_session_expired":
      message = t("guest.errors.sessionExpired");
      retryable = false;
      break;
    // The stop-cold codes normally swap the composer for the sign-up card
    // (W2.5), and `guest_claimed` re-bootstraps the tab (W2.4c, B-2), instead
    // of reaching this slot; worded here for completeness.
    case "guest_limit_reached":
      message = t("guest.gate.titleTokens");
      retryable = false;
      break;
    case "guest_paused":
      message = t("guest.gate.titlePaused");
      retryable = false;
      break;
    case "guest_claimed":
      message = t("guest.gate.titleClaimed");
      retryable = false;
      break;
    case "guest_disabled":
      message = t("guest.gate.titleDisabled");
      retryable = false;
      break;
    default:
      message = t("errors.generic");
      break;
  }
  return (
    <div
      role="alert"
      data-testid="chat-error-slot"
      className="flex flex-col gap-2.5 rounded-[var(--g-radius)] border border-[color-mix(in_srgb,var(--g-error)_30%,transparent)] bg-[color-mix(in_srgb,var(--g-error)_7%,transparent)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <span className="flex items-start gap-2.5 text-[15px] leading-snug text-foreground">
        <span className="mt-px shrink-0 text-[var(--g-error)]">
          <RotateCw {...iconProps("sm")} />
        </span>
        <span>{message}</span>
      </span>
      {retryable && onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          data-testid="chat-error-retry"
          className="inline-flex h-9 shrink-0 items-center gap-1.5 self-start rounded-[10px] bg-primary px-3.5 text-[14px] font-semibold text-[var(--g-on-primary)] transition-transform duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none sm:self-auto"
        >
          <RotateCw {...iconProps("sm")} />
          {t("errors.retry")}
        </button>
      ) : null}
    </div>
  );
}

export default MessageList;
